// Info icon button whose tooltip opens on hover, keyboard focus AND tap, and closes on blur, Escape or
// an outside tap. The DS Tooltip alone is hover/focus-only, never opens on touch, and closes on any
// click on its trigger (Enter/Space included), so this drives `open` itself.
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactElement } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@lmn/design-system';
import { Icon, type IconColor } from '@/app/common/icons/Icon';
import { focusRing } from '@/app/components/search/styles/mixins';

const TooltipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:focus-visible {
    ${focusRing}
  }
`;

// Time for the mouse to cross the 9 px gap onto the bubble, a portal child of TooltipWrapper in React's tree.
const HOVER_CLOSE_DELAY_MS = 300;

type InfoTooltipButtonProps = {
  content: ReactElement | string;
  iconColor: IconColor;
  maxWidth?: number;
} & Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'aria-describedby'>;

// Only the aria props a caller passes are spread: an explicit `aria-describedby={undefined}` would
// override the one Radix sets on the trigger while open.
export function InfoTooltipButton({ content, iconColor, maxWidth, ...ariaProps }: InfoTooltipButtonProps) {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const show = () => {
    setLeaving(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => {
      setLeaving(false);
      // Keyboard focus keeps it open until blur (1.4.13 persistent); a mouse click focuses without :focus-visible.
      if (!buttonRef.current?.matches(':focus-visible')) setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event: Event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    // WCAG 1.4.13: Radix's own Escape is a no-op as the DS Tooltip is controlled without onOpenChange,
    // and the bubble can be open while focus is elsewhere (hover, or a click in Safari).
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  return (
    <TooltipWrapper
      ref={wrapperRef}
      // Mouse only: on touch Chromium fires a synthetic mouseleave right after the tap, which would
      // close it. onBlur / outside tap dismiss on touch.
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') show();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') setLeaving(true);
      }}
    >
      <Tooltip content={content} placement="top" maxWidth={maxWidth} open={open}>
        <InfoButton
          {...ariaProps}
          ref={buttonRef}
          type="button"
          onClick={show}
          onFocus={show}
          onBlur={() => setOpen(false)}
        >
          <Icon category="Semantics" name="icon-circle-info" size={16} color={iconColor} alt="" />
        </InfoButton>
      </Tooltip>
    </TooltipWrapper>
  );
}
